/**
 * Help section copy (German). Same keys as helpSections.en.ts
 */
import type { HelpSectionMap } from './helpSections.en';

const helpSectionsDe: HelpSectionMap = {
  'getting-started': {
    intro:
      'Hier ist alles, um loszulegen. Folgen Sie diesen Schritten oder nutzen Sie die Seitenleiste.',
    stepsTitle: 'Ihre ersten Schritte',
    step1: 'Öffnen Sie **Übersicht**, um Ihr Dashboard und letzte Aktivität zu sehen.',
    step2: 'Öffnen Sie **Meine Aufgaben**, um alle Ihnen zugewiesenen Tickets zu sehen.',
    step3:
      'Klicken Sie in der Seitenleiste auf ein **Projekt**, um Board, Backlog, Roadmap und mehr zu öffnen.',
    stepPm:
      'Öffnen Sie **Projektmanagement**, um alle Projekte und eine Roadmap-Übersicht (aktueller Stand, nächstes Deployment, geplantes Datum) zu sehen.',
    step4: 'Nutzen Sie die Schaltfläche **Erstellen** in der Kopfzeile, um ein neues Ticket anzulegen.',
    step5:
      'Nutzen Sie den **Themen-Schalter** (Sonne/Mond) in der Kopfzeile für Hell- oder Dunkelmodus.',
    outro:
      'Nutzen Sie die Links links, um zu einem Thema zu springen, oder die kurze Tour oben.',
  },
  notifications: {
    intro:
      'Das Glocken-Symbol zeigt In-App-Benachrichtigungen. Klicken Sie, um das Benachrichtigungs-Center zu öffnen und Einträge als gelesen zu markieren.',
    whatTitle: 'Wofür Sie Benachrichtigungen erhalten',
    n1: 'Wenn Ihnen jemand ein Ticket zuweist',
    n2: 'Wenn Sie in einem Kommentar @erwähnt werden',
    n3: 'Statusänderungen, Kommentare und Fälligkeitserinnerungen',
    n4: 'Projekteinladungen (falls Ihre Organisation sie nutzt)',
    prefsTitle: 'Einstellungen',
    prefsBody:
      'In Profil oder Kontoeinstellungen wählen Sie, welche Benachrichtigungen Sie per E-Mail und in der App erhalten (z. B. nur Zuweisungen oder auch Erwähnungen).',
    sessionsBody:
      'Im Profil sehen Sie auch **Aktive Sitzungen** und können andere Geräte abmelden.',
    tip: 'Öffnen Sie die Glocke und klicken Sie auf „Alle als gelesen markieren“, um die Liste zu leeren.',
  },
  'keyboard-shortcuts': {
    intro:
      'Diese Kürzel funktionieren, wenn der Hauptinhalt fokussiert ist. Auf dem Mac **Cmd**, unter Windows/Linux **Strg**.',
    k1: '**Cmd/Strg + K** oder **/** – Tickets und Projekte suchen (Spotlight). Deaktiviert, wenn ein Ticket-Modal offen ist.',
    k2: '**Cmd/Strg + I** – Ticket erstellen öffnen',
    k3: '**Cmd/Strg + Enter** – Formular „Ticket erstellen“ absenden',
    k4: '**Cmd/Strg + S** – Speichern (im Dokument-Editor)',
    k5: '**Escape** – Modal oder Suche schließen',
    tip: 'Mit / oder Cmd/Strg + K finden Sie Tickets oder Projekte ohne die Seite zu verlassen. Die Listenansicht nutzt Virtualisierung für große Listen.',
  },
  projects: {
    intro:
      'Projekte enthalten Ihre Tickets, Sprints und Berichte. Sie öffnen jedes Projekt, in dem Sie Mitglied sind, über die Seitenleiste.',
    createTitle: 'Projekt anlegen',
    createBody:
      'Gehen Sie zu **Projektmanagement** und klicken Sie auf **Projekt anlegen**. Name, Beschreibung und Kategorie. Optional eine Vorlage für Workflows und Einstellungen.',
    membersTitle: 'Mitglieder verwalten',
    membersLead:
      'Öffnen Sie **Projektmanagement** für alle Projekte, CSV-Export und **Roadmap-Übersicht** (aktueller Stand, nächstes Deployment, geplantes Datum pro Projekt). Öffnen Sie ein Projekt und **Mitglieder verwalten**, um:',
    m1: 'Personen hinzuzufügen und Rollen zu setzen (Admin, Mitglied, Betrachter).',
    m2:
      'Eine **Gruppe** hinzuzufügen (unter Admin-Einstellungen → Gruppen), damit alle dieselbe Rolle erhalten.',
    m3: 'Rollen zu ändern oder Personen zu entfernen.',
    rolesHint:
      'Betrachter = nur lesen. Mitglied = Tickets bearbeiten und kommentieren. Admin = Mitglieder und Einstellungen. Mindestens ein Projekt-Admin nötig.',
    notePlan:
      'Die Projekterstellung kann durch den Organisationsplan (Free, Pro, Enterprise) begrenzt sein. Siehe Pläne & Limits.',
    memberOnly:
      'Nur Admins und Projektmanager können Projekte anlegen. Bitte Admin kontaktieren oder Zugriff anfragen.',
  },
  issues: {
    intro:
      'Tickets sind die Arbeitseinheiten: Aufgaben, Bugs, Stories, Epics und Unteraufgaben.',
    createTitle: 'Ticket anlegen',
    c1: 'Klicken Sie oben auf **Erstellen**.',
    c2: 'Wählen Sie **Ticket anlegen** oder **Massenimport aus CSV**.',
    c3: 'Formular ausfüllen (Projekt, Titel, Typ, Fälligkeit usw.) und absenden.',
    createTip: 'Tickets können Sie auch vom Board oder aus dem Backlog anlegen.',
    typesTitle: 'Tickettypen',
    typeTask: '**Aufgabe** – Allgemeine Arbeit',
    typeStory: '**Story** – Nutzerfunktion',
    typeBug: '**Bug** – Fehlerbehebung',
    typeEpic: '**Epic** – Große Initiative; andere Tickets können verlinken',
    typeSub: '**Unteraufgabe** – Kind eines anderen Tickets',
    fieldsTitle: 'Was Sie setzen können',
    fieldsBody:
      'Titel, Beschreibung, Bearbeiter, Priorität, Status und Fälligkeit sind die Basis. Zusätzlich Labels, Komponenten, Versionen, Eltern- oder Epic-Link, Schätzungen und Anhänge. Beim Anlegen können Sie ein **verknüpftes Ticket** wählen (z. B. blockiert, wird blockiert von, bezieht sich auf, Duplikat); der Typ wird als Standard gemerkt. Im Ticket bearbeiten und kommentieren.',
  },
  backlog: {
    intro:
      'Alle Tickets außerhalb eines Sprints und Sprint-Verwaltung. Ziehen Sie Tickets zum Sortieren oder in einen Sprint.',
    b1: 'Ziehen Sie Tickets in einen Sprint, um Arbeit zu planen.',
    b2: 'Sprints anlegen und von geplant → aktiv → abgeschlossen verschieben.',
    b3: 'Tickets bei Bedarf zurück in den Backlog legen.',
    b4:
      'Große Backlogs nutzen Virtualisierung; Drag-and-drop behält die Reihenfolge bei.',
  },
  'project-management': {
    intro:
      'Für Admins und Projektmanager. **Projektmanagement** in der Seitenleiste: alle Projekte und Roadmap auf hoher Ebene.',
    allTitle: 'Alle Projekte',
    allBody:
      'Die Tabelle listet alle Projekte der Organisation. Neues Projekt, CSV-Export, Board, Backlog, Roadmap oder Dokumente. **Mitglieder verwalten** pro Projekt für Rollen.',
    archivedBody:
      '**Archivierte anzeigen** einblenden. Org- und Projekt-Admins archivieren oder stellen unter **Aktionen** wieder her — archivierte Projekte erscheinen standardmäßig nicht in Listen.',
    roadmapTitle: 'Roadmap-Übersicht',
    roadmapLead: 'Oben zeigt die **Roadmap-Übersicht** pro Projekt:',
    ro1: '**Aktueller Stand** – zuletzt veröffentlichte Version (falls vorhanden).',
    ro2: '**Nächstes Deployment** – Name der nächsten geplanten Version.',
    ro3: '**Geplantes Datum** – Veröffentlichungsdatum dieser Version, falls gesetzt.',
    roadmapFoot:
      'So sehen Sie Stand und nächste Releases. Klick auf den Projektnamen oder **Vollständige Roadmap**, um die organisationweite oder projektbezogene Roadmap zu öffnen.',
    roadmapNote:
      'Versionen und Termine pro Projekt konfigurieren (Admin-Einstellungen oder Projekt). Versionen anlegen und **Fix-Version** setzen, um die Roadmap zu füllen.',
  },
  roadmap: {
    intro:
      'Roadmaps zeigen Versionen und Releases. FlowBoard hat eine projektbezogene und eine organisationweite Roadmap.',
    projTitle: 'Projekt-Roadmap',
    projLead: 'Projekt öffnen und **Roadmap** in der Seitenleiste. Sie sehen:',
    pr1:
      'Eine Karte mit **Aktueller Stand**, **Nächstes Deployment** und **Geplantes Datum**.',
    pr2:
      '**Listenansicht** – Zeitlinie der Versionen mit Beschreibung, Start-/Releasedaten und Tickets (Fix- / Betroffene Version).',
    pr3:
      '**Zeitachsenansicht** – Versionen als Balken; Ticket-Badges ziehen, um die **Fix-Version** zu ändern.',
    fixBody:
      'Versionen im Projekt (oder Org) anlegen und **Fix-Version** / Betroffene Version setzen.',
    crossTitle: 'Organisations-Roadmap',
    crossBody:
      'In der Seitenleiste **Organisations-Roadmap**: alle Versionen aller Projekte. Zeile zeigt Projekt, Version, Datum, Ticketzahlen (Fix / Betroffen). Klick auf den Projektnamen öffnet die Projekt-Roadmap.',
    tip: 'Nutzen Sie die Roadmap für Abstimmung zu Stand und Releases. Start- und Releasedaten für Versionen setzen.',
  },
  calendar: {
    body: 'Tickets mit Fälligkeitsdatum erscheinen im Kalender. Monate wechseln, um vorauszuplanen oder Überfälliges zu sehen.',
  },
  'list-view': {
    intro:
      'Tabelle aller Projekt-Tickets. Sortieren, filtern und Massenbearbeitung von Status oder Bearbeiter.',
    lv1: 'Erweiterte Filter nach Status, Bearbeiter, Label usw.',
    lv2: 'Mehrere Tickets wählen, um Status, Bearbeiter oder Löschen in einem Schritt zu ändern.',
    lvBulk:
      'Massenbearbeitung ändert viele Tickets auf einmal. In der Projektliste auch Labels, Komponenten, Fix-Versionen und Lösung.',
    lv3:
      'Bei großen Listen **Seitennummerierung** unten nutzen und Einträge pro Seite steuern.',
    lv4:
      '**Gespeichertes Layout** speichert Filter, Suche, Sortierung und Spalten lokal (getrennt von serverseitigen gespeicherten Filtern). Spalte **SLA** für Verletzungen einblenden.',
    tip: 'Listenansicht eignet sich zum Vergleich vieler Tickets und Massenaktionen.',
  },
  'dependency-graph': {
    body: 'Visuelle Darstellung verknüpfter Tickets (blockiert, wird blockiert von, bezieht sich auf). Für Auswirkungen und kritische Pfade.',
  },
  docs: {
    intro:
      'Jedes Projekt hat einen Bereich **Dokumente** (ähnlich Confluence) für Spezifikationen und Notizen. Projekt öffnen und **Dokumente** in der Seitenleiste.',
    tplTitle: 'Vorlagen und Labels',
    tplBody:
      'Beim Erstellen oder Bearbeiten wählen Sie eine **Vorlage** (z. B. Meeting, Spec) und **Labels** zur Organisation.',
    sidebarTitle: 'Linke Seitenleiste',
    sidebarBody:
      'Baum aller Seiten links. **Neue Seite** anlegen, Suchfeld zum Filtern. Ordner ein-/ausklappen; Titel klicken zum Öffnen.',
    pageTitle: 'Auf der Seite',
    d1: '**Auf dieser Seite** – Inhaltsverzeichnis aus H2/H3 beim Ansehen.',
    d2: '**Unterseiten** – Verlinkte Unterseiten unter dem Inhalt.',
    d3: '**Bearbeiten** – Titel, übergeordnete Seite, Inhalt. Toolbar für Formatierung, Links, Bilder.',
    d4: '**Verlauf** – Versionen ansehen und wiederherstellen.',
    d5: '**Export** – Seite als Markdown herunterladen.',
    d6: 'Kommentare unten; Antworten lesen und schreiben.',
    tip: '**Strg + S** im Dokument-Editor speichert ohne die Seite zu verlassen.',
  },
  reports: {
    intro:
      'Unter **Berichte**: Velocity, Kumulativer Fluss, Regelkarte, Epic-Fortschritt, Zeiterfassung, Report-Builder und **Workflow** (Gantt). Daten oder Chart exportieren.',
    typesTitle: 'Berichtstypen',
    r1: '**Velocity** – Erledigte Arbeit pro Sprint.',
    r2: '**Kumulativer Fluss** – Bewegung durch Status über die Zeit.',
    r3: '**Regelkarte** – Durchlaufzeit abgeschlossener Tickets.',
    r4:
      '**Epic-Bericht** – **Epic**-Dropdown für ein Epic oder alle; Export über die Schaltfläche.',
    r5: '**Zeiterfassung** – Schätzung vs. gebucht nach Typ und Nutzer.',
    r6:
      '**Workflow (Gantt)** – Zeitachse mit Fälligkeiten: Hierarchie, Fortschritt, Abhängigkeiten, Meilensteine. Zoom (Tag/Woche/Monat), **Als Bild exportieren** (PNG). Farben folgen Projekt-Statusfarben.',
    r7: '**Report-Builder** – Gruppierung nach Status, Typ, Priorität oder Bearbeiter.',
    tip: 'Velocity und kumulativer Fluss zeigen Durchsatz und Engpässe.',
  },
  dashboards: {
    body: '**Eigene Dashboards**: Widgets nach Projekt, Gruppe oder Bearbeiter. Sichtbarkeit nach Gruppe oder Rolle.',
  },
  'admin-settings': {
    intro:
      '**Admin-Einstellungen** in der Seitenleiste: Organisation und Projekt-Setup.',
    orgTitle: 'Organisation',
    orgBody: 'Org-Name, Mitglieder und ausstehende Einladungen.',
    projCfgTitle: 'Projektkonfiguration',
    projCfgBody:
      '**Labels**, **Komponenten**, **Versionen** und **Benutzerdefinierte Felder** organisationweit.',
    tplTitle: 'Vorlagen',
    tplBody:
      '**Projekt-** und **Ticket-Vorlagen** für Standard-Workflows und Felder.',
    autoTitle: 'Automatisierung',
    autoBody:
      'Regeln bei Bedingungen (z. B. Status, Bearbeiter). Trigger und Aktionen. **Bearbeiten** (Stift) in der Tabelle für Name, Beschreibung, Trigger, aktiv; **Löschen** entfernt die Regel.',
    groupsTitle: 'Gruppen',
    groupsBody:
      'Gruppen anlegen, Mitglieder zuweisen, in Projekten (Mitglieder verwalten) dieselbe Rolle für alle in der Gruppe.',
    rolesTitle: 'Rollen & Audit-Log',
    rolesBody: 'Eigene Rollen und Audit-Log für wichtige Änderungen.',
    systemTitle: 'System (nur Super-Admin)',
    systemBody:
      'Registerkarte **System**: alle Organisationen, **Plan** (Free, Pro, Enterprise) und neue Unternehmen einladen.',
  },
  glossary: {
    intro: 'Kurze Begriffe in FlowBoard.',
    g1: '**Ticket** – Arbeitseinheit: Aufgabe, Story, Bug, Epic, Unteraufgabe.',
    g2: '**Epic** – Große Initiative; Verknüpfung anderer Tickets.',
    g3: '**Sprint** – Zeitlich begrenztes Arbeitspaket (z. B. 2 Wochen).',
    g4: '**Backlog** – Tickets noch nicht in einem Sprint.',
    g5: '**Version** – Release oder Meilenstein; optional Daten; kann „Released“ sein.',
    g6: '**Fix-Version** – Geplante Auslieferung; in der Roadmap sichtbar.',
    g7: '**Betroffene Version** – Von einem Ticket betroffene Versionen (z. B. Bugs).',
    g8: '**Kanban-Board** – Spalten nach Status; Karten ziehen.',
    g9: '**Workflow** – Status und Übergänge.',
    g10: '**Bearbeiter** – Verantwortliche Person.',
    g11: '**Beobachter** – Wer über Updates informiert wird.',
  },
  'subscription-plans': {
    body: 'Der Free-Plan begrenzt Projekte und Nutzer; höhere Pläne erlauben mehr. Bei Limit keine neuen Einträge bis Upgrade. Super-Admins setzen den Plan unter Admin → System.',
  },
  'api-integrations': {
    intro:
      'FlowBoard bietet eine REST-API für Tickets, Projekte, Berichte, Webhooks u. a. Basis-URL der Instanz mit gültigem Token verwenden.',
    webhooksTitle: 'Webhooks',
    webhooksBody:
      'Org-Admins konfigurieren Webhooks unter **Admin → Webhooks**. HTTP POST bei Ereignissen; optional HMAC-Geheimnis. Für Slack: Incoming-Webhook-URL oder Automatisierung mit Webhook-Aktion.',
    openapiNote:
      'OpenAPI 3.0 unter **/api/docs/openapi.json**, wenn das Backend läuft; für Swagger oder andere Tools.',
  },
};

export default helpSectionsDe;
